import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import {
    X,
    Menu,
    Map as MapIcon,
    Mountain,
    Newspaper,
    BookOpen,
    HelpCircle,
    Mail,
    Info,
    Share2,
    ChevronRight,
    ChevronDown,
    LogOut,
    LogIn,
    UserCircle2,
    CalendarDays,
    Telescope,
    BellRing,
    Moon,
    Radar,
    FileText,
    ShieldCheck,
    Scale
} from 'lucide-react';
import { Share } from '@capacitor/share';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';
import { supportedLanguages } from '../../i18n';
import { useAuth } from '@/hooks/useAuth';
import { useSiteSettings } from '@/hooks/useSiteSettings';
import { Flag } from './ui/Flag';


interface MobileMenuProps {
    isOpen?: boolean;
    onClose?: () => void;
    toggle?: () => void;
    hideButton?: boolean;
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ 
    isOpen: controlledIsOpen, 
    onClose: controlledOnClose,
    toggle: controlledToggle,
    hideButton = false
}) => {
    const { t, i18n } = useTranslation();
    const { user } = useAuth();
    const { sentinelPublic } = useSiteSettings();
    const [internalIsOpen, setInternalIsOpen] = useState(false);

    // Sections repliables du menu, état mémorisé entre ouvertures (le menu se ferme à chaque navigation).
    // Défaut : Sentinel + Outils ouverts, Découvrir replié.
    const [openSections, setOpenSections] = useState<{ sentinel: boolean; tools: boolean; discover: boolean }>(() => {
        const defaults = { sentinel: true, tools: true, discover: false };
        try {
            const raw = localStorage.getItem('ssi_menu_sections');
            if (raw) return { ...defaults, ...JSON.parse(raw) };
        } catch { /* localStorage indisponible : on garde les défauts */ }
        return defaults;
    });
    const toggleSection = (id: 'sentinel' | 'tools' | 'discover') => {
        setOpenSections(prev => {
            const next = { ...prev, [id]: !prev[id] };
            try { localStorage.setItem('ssi_menu_sections', JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    };

    const isOpen = controlledIsOpen !== undefined ? controlledIsOpen : internalIsOpen;
    const setIsOpen = controlledIsOpen !== undefined ? (val: boolean) => !val && controlledOnClose?.() : setInternalIsOpen;

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            // Scroll to top of menu when opening
        } else {
            document.body.style.removeProperty('overflow');
        }
        return () => {
            document.body.style.removeProperty('overflow');
        };
    }, [isOpen]);

    const toggleMenu = () => {
        if (controlledToggle) {
            controlledToggle();
        } else if (controlledIsOpen !== undefined) {
             if (isOpen) controlledOnClose?.();
        } else {
            setInternalIsOpen(!internalIsOpen);
        }
    };

    const handleShare = async () => {
        try {
            await Share.share({
                title: 'StellarSkyImager',
                text: t('planner.mobile.about.shareText'),
                url: isNative
                    ? 'https://play.google.com/store/apps/details?id=fr.stellarskyimager.planner' // Lien Play Store pour l'app
                    : 'https://www.stellarskyimager.fr', // Site web
                dialogTitle: t('planner.mobile.menu.share'),
            });
        } catch (error) {
            console.error('Error sharing', error);
        }
    };

    const handleExit = async () => {
        await App.exitApp();
    };

    const isNative = Capacitor.isNativePlatform();

    const iconSize = isNative ? 20 : 22;
    // Groupes alignés sur le web : « Outils » (Planner + Horizon Generator) et « Découvrir » (contenu).
    const toolsItems = [
        { path: '/planner', icon: <MapIcon size={iconSize} />, label: t('planner.mobile.menu.planner') },
        { path: '/horizon-generator', icon: <Mountain size={iconSize} />, label: t('planner.mobile.menu.horizonGenerator') },
        // Sentinel contextuel : connecté → l'outil, sinon → la landing publique (cf. Header).
        // [Interrupteur Sentinel] Fermé + anonyme → entrée masquée (elle mènerait à la landing/promo).
        ...((user || sentinelPublic) ? [{ path: user ? '/sentinel/projects' : '/sentinel', icon: <Radar size={iconSize} />, label: t('planner.mobile.menu.sentinel') }] : []),
    ];
    // « Découvrir » = contenu éditorial uniquement.
    const discoverItems = [
        { path: '/news', icon: <Newspaper size={iconSize} />, label: t('planner.mobile.menu.news') },
        ...(isNative ? [{ path: '/evenements', icon: <CalendarDays size={20} />, label: t('planner.mobile.menu.events') }] : []),
        { path: '/guides', icon: <BookOpen size={iconSize} />, label: t('planner.mobile.menu.guides') },
    ];
    // Liens support/institutionnels — hors groupes repliables, en zone secondaire (bas du menu).
    const secondaryItems = [
        { path: '/help', icon: <HelpCircle size={iconSize} />, label: t('planner.mobile.menu.help') },
        { path: '/contact', icon: <Mail size={iconSize} />, label: t('planner.mobile.menu.contact') },
        { path: '/about', icon: <Info size={iconSize} />, label: t('planner.mobile.menu.about') },
        // Pages légales : sur le web elles sont dans le pied de page, absent de l'app native.
        ...(isNative ? [
            { path: '/cgv', icon: <FileText size={iconSize} />, label: t('footer.cgv') },
            { path: '/confidentialite', icon: <ShieldCheck size={iconSize} />, label: t('footer.privacy') },
            { path: '/mentions-legales', icon: <Scale size={iconSize} />, label: t('footer.legal') },
        ] : []),
    ];
    const itemClass = ({ isActive }: { isActive: boolean }) =>
        `flex items-center rounded-xl transition-all ${isNative ? 'gap-3 py-2.5 px-4' : 'gap-4 p-4'} ${isActive ? 'bg-primary/10 text-primary border border-primary/20' : 'text-zinc-400 hover:bg-zinc-900 border border-transparent'}`;
    const sectionHeadingClass = `px-2 ${isNative ? 'pt-2 pb-0.5' : 'pt-3 pb-1'} text-[11px] font-bold uppercase tracking-wider text-zinc-500`;
    const renderItem = (item: { path: string; icon: React.ReactNode; label: string }) => (
        <NavLink key={item.path} to={item.path} onClick={toggleMenu} className={itemClass}>
            <span className="text-zinc-500">{item.icon}</span>
            <span className={`${isNative ? 'text-base' : 'text-lg'} font-medium flex-1 min-w-0 truncate`}>{item.label}</span>
            <ChevronRight size={isNative ? 16 : 18} className="text-zinc-600 shrink-0" />
        </NavLink>
    );
    // Groupe repliable : titre cliquable + chevron, contenu masqué quand replié (état persistant).
    const section = (id: 'sentinel' | 'tools' | 'discover', title: string, content: React.ReactNode) => (
        <div>
            <button
                type="button"
                onClick={() => toggleSection(id)}
                className={`${sectionHeadingClass} w-full flex items-center justify-between hover:text-zinc-300 transition-colors`}
            >
                <span>{title}</span>
                <ChevronDown size={16} className={`transition-transform ${openSections[id] ? '' : '-rotate-90'}`} />
            </button>
            {openSections[id] && (
                // Indentation + filet vertical : les enfants apparaissent rattachés à leur titre,
                // et se distinguent des entrées de la zone secondaire (alignées à gauche).
                <div className={`mt-1 ml-3 pl-2 border-l border-zinc-800/70 ${isNative ? 'space-y-1' : 'space-y-2'}`}>{content}</div>
            )}
        </div>
    );

    return (
        <>
            {/* Hamburger Button */}
            {!hideButton && (
                <button
                    onClick={toggleMenu}
                    className="p-2 text-zinc-300 hover:text-white transition-colors"
                    aria-label="Open menu"
                >
                    <Menu size={28} />
                </button>
            )}

            {/* Full Screen Overlay */}
            {isOpen && (
                <div className="fixed inset-0 z-[100] bg-zinc-950 flex flex-col animate-in fade-in slide-in-from-left duration-300 overscroll-contain overscroll-y-none select-none overflow-x-hidden">
                    {/* Header */}
                    <div className={`flex items-center justify-between border-b border-zinc-800/50 touch-none ${isNative ? 'p-3' : 'p-4'}`}>
                        <span className="text-xl font-bold bg-gradient-to-r from-primary to-blue-400 bg-clip-text text-transparent">
                            SSI MENU
                        </span>
                        <button
                            onClick={toggleMenu}
                            className="p-2 text-zinc-400 hover:text-white"
                        >
                            <X size={isNative ? 24 : 28} />
                        </button>
                    </div>

                    {/* Navigation Links */}
                    <nav className={`flex-1 overflow-y-auto px-4 scrollbar-hide ${isNative ? 'py-3 space-y-1' : 'py-6 space-y-2'}`}>
                        {/* Section Sentinel (compte + projets) — le compte n'existe que pour Sentinel */}
                        {section('sentinel', t('planner.mobile.menu.sectionSentinel'),
                            user ? (
                                <>
                                    <NavLink to="/account" onClick={toggleMenu} className={itemClass}>
                                        <span className="relative text-zinc-500">
                                            <UserCircle2 size={iconSize} />
                                            <span className="absolute -top-0.5 -right-0.5 w-1.5 h-1.5 rounded-full bg-emerald-400" />
                                        </span>
                                        <span className={`${isNative ? 'text-base' : 'text-lg'} font-medium flex-1 min-w-0 truncate`}>{t('auth.header.account')}</span>
                                        <ChevronRight size={isNative ? 16 : 18} className="text-zinc-600 shrink-0" />
                                    </NavLink>
                                    <NavLink to="/sentinel/tonight" onClick={toggleMenu} className={itemClass}>
                                        <span className="text-zinc-500"><Moon size={iconSize} /></span>
                                        <span className={`${isNative ? 'text-base' : 'text-lg'} font-medium flex-1 min-w-0 truncate`}>{t('sentinel.tonight.pageTitle')}</span>
                                        <ChevronRight size={isNative ? 16 : 18} className="text-zinc-600 shrink-0" />
                                    </NavLink>
                                    <NavLink to="/sentinel/calendar" onClick={toggleMenu} className={itemClass}>
                                        <span className="text-zinc-500"><CalendarDays size={iconSize} /></span>
                                        <span className={`${isNative ? 'text-base' : 'text-lg'} font-medium flex-1 min-w-0 truncate`}>{t('sentinel.calendar.menuTitle')}</span>
                                        <ChevronRight size={isNative ? 16 : 18} className="text-zinc-600 shrink-0" />
                                    </NavLink>
                                    <NavLink to="/sentinel/projects" onClick={toggleMenu} className={itemClass}>
                                        <span className="text-zinc-500"><Telescope size={iconSize} /></span>
                                        <span className={`${isNative ? 'text-base' : 'text-lg'} font-medium flex-1 min-w-0 truncate`}>{t('sentinel.projects.title')}</span>
                                        <ChevronRight size={isNative ? 16 : 18} className="text-zinc-600 shrink-0" />
                                    </NavLink>
                                    <NavLink to="/sentinel/alerts" onClick={toggleMenu} className={itemClass}>
                                        <span className="text-zinc-500"><BellRing size={iconSize} /></span>
                                        <span className={`${isNative ? 'text-base' : 'text-lg'} font-medium flex-1 min-w-0 truncate`}>{t('sentinel.alerts.title')}</span>
                                        <ChevronRight size={isNative ? 16 : 18} className="text-zinc-600 shrink-0" />
                                    </NavLink>
                                </>
                            ) : (
                                <NavLink to="/login" onClick={toggleMenu} className={itemClass}>
                                    <span className="text-zinc-500"><LogIn size={iconSize} /></span>
                                    <span className={`${isNative ? 'text-base' : 'text-lg'} font-medium flex-1 min-w-0 truncate`}>{t('auth.header.login')}</span>
                                    <ChevronRight size={isNative ? 16 : 18} className="text-zinc-600 shrink-0" />
                                </NavLink>
                            )
                        )}

                        {/* Section Outils — Planner + Horizon Generator (comme sur le web) */}
                        {section('tools', t('planner.mobile.menu.sectionTools'), toolsItems.map(renderItem))}

                        {/* Section Découvrir — contenu éditorial (actus, événements, guides) */}
                        {section('discover', t('planner.mobile.menu.sectionDiscover'), discoverItems.map(renderItem))}

                        {/* Zone secondaire (support & institutionnel) — hors groupes repliables */}
                        <div className={`border-t border-zinc-800/50 ${isNative ? 'my-1.5' : 'my-2'}`} />
                        {secondaryItems.map(renderItem)}

                        {/* Share Button (Special) */}
                        <button
                            onClick={handleShare}
                            className={`w-full flex items-center rounded-xl text-zinc-400 hover:bg-zinc-900 transition-all border border-transparent ${isNative ? 'gap-3 py-2.5 px-4' : 'gap-4 p-4'}`}
                        >
                            <span className="text-zinc-500">
                                <Share2 size={isNative ? 20 : 22} />
                            </span>
                            <span className={`${isNative ? 'text-base' : 'text-lg'} font-medium flex-1 text-left min-w-0 truncate`}>{t('planner.mobile.menu.share')}</span>
                            <ChevronRight size={isNative ? 16 : 18} className="text-zinc-600 shrink-0" />
                        </button>

                        {/* Exit Button (Native Only) */}
                        {isNative && (
                            <button
                                onClick={handleExit}
                                className="w-full flex items-center gap-3 py-2.5 px-4 rounded-xl text-red-400 hover:bg-red-400/10 transition-all border border-transparent"
                            >
                                <span className="text-red-500">
                                    <LogOut size={20} />
                                </span>
                                <span className="text-base font-medium flex-1 text-left min-w-0 truncate">{t('planner.mobile.menu.exit')}</span>
                                <ChevronRight size={16} className="text-zinc-600 shrink-0" />
                            </button>
                        )}
                    </nav>

                    {/* Footer with Language Selector */}
                    <div className="p-6 border-t border-zinc-900 bg-zinc-900/30 touch-none">
                        <div className="text-sm text-zinc-500 mb-4 uppercase tracking-wider font-semibold">
                            {t('language')}
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            {supportedLanguages.map((lang) => (
                                <button
                                    key={lang.code}
                                    onClick={() => i18n.changeLanguage(lang.code)}
                                    className={`
                    flex items-center gap-2 p-3 rounded-lg border transition-all text-sm
                    ${i18n.language === lang.code
                                            ? 'bg-primary/10 border-primary/30 text-primary'
                                            : 'bg-zinc-800/50 border-zinc-700/50 text-zinc-400 hover:bg-zinc-800'}
                  `}
                                >
                                    <Flag langCode={lang.code} size={18} />
                                    <span className="truncate">{lang.name}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

